/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Angepasst für solv.it Kundenterminal:
* Profilseite mit Kundennummer, Rechnungsadresse & Zahlungsinformationen
=========================================================
*/

import { useState, useEffect } from "react";
import { apiFetch } from "utils/api";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

const defaultProfile = {
  avatarUrl: "",
  customerNumber: "",
  companyName: "",
  contactName: "",
  street: "",
  zip: "",
  city: "",
  country: "Deutschland",
  vatId: "",
  accountHolder: "",
  iban: "",
  bic: "",
};

function safeParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function Overview() {
  const [profile, setProfile] = useState(defaultProfile);
  const [userRole, setUserRole] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Beim Mount Profil vom Backend laden
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const me = await apiFetch("/api/me");
        const c = me?.customer || {};
        const role = String(me?.role || "").toLowerCase();

        const next = {
          ...defaultProfile,
          avatarUrl: me?.avatarUrl || "",
          customerNumber: c.customerNumber || "",
          companyName: c.companyName || "",
          contactName: c.contactName || "",
          street: c.street || "",
          zip: c.zip || "",
          city: c.city || "",
          country: c.country || "Deutschland",
          vatId: c.vatId || "",
          accountHolder: c.accountHolder || "",
          iban: c.iban || "",
          bic: c.bic || "",
        };

        if (!cancelled) {
          setUserRole(role);
          setUserEmail(me?.email || "");
        }
        if (!cancelled) setProfile(next);
        try {
          const raw = localStorage.getItem("solvit_profile");
          const parsed = raw ? safeParse(raw) : {};
          localStorage.setItem("solvit_profile", JSON.stringify({ ...(parsed || {}), avatarUrl: next.avatarUrl }));
          window.dispatchEvent(new Event("solvit-profile-updated"));
        } catch {
          // ignore storage update errors
        }
      } catch (e) {
        console.error("Fehler beim Laden des Profils (API)", e);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolveAssetUrl = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    if (typeof window === "undefined") return url;
    return `${window.location.origin}${url}`;
  };

  const isAdmin = userRole === "admin";

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploadingAvatar(true);
    try {
      const uploaded = await apiFetch("/api/me/avatar", {
        method: "POST",
        body: formData,
      });
      const avatarUrl = uploaded?.avatarUrl || "";
      setProfile((prev) => ({ ...prev, avatarUrl }));
      try {
        const raw = localStorage.getItem("solvit_profile");
        const parsed = raw ? safeParse(raw) : {};
        localStorage.setItem("solvit_profile", JSON.stringify({ ...(parsed || {}), avatarUrl }));
        window.dispatchEvent(new Event("solvit-profile-updated"));
      } catch {
        // ignore storage update errors
      }
    } catch (e) {
      console.error("Fehler beim Upload des Profilbilds", e);
      alert("Profilbild konnte nicht hochgeladen werden.");
    } finally {
      setUploadingAvatar(false);
      event.target.value = "";
    }
  };

  const handleChange = (field) => (event) => {
    setProfile((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/me/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: {
          companyName: profile.companyName,
          contactName: profile.contactName,
          street: profile.street,
          zip: profile.zip,
          city: profile.city,
          country: profile.country,
          vatId: profile.vatId,
          accountHolder: profile.accountHolder,
          iban: profile.iban,
          bic: profile.bic,
        },
      });

      setSavedAt(new Date());
    } catch (e) {
      console.error("Fehler beim Speichern des Profils (API)", e);
      alert("Konnte dein Profil nicht speichern. Bitte versuche es später erneut.");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();

    if (!newPassword || newPassword !== confirmPassword) {
      alert("Die neuen Passwörter stimmen nicht überein.");
      return;
    }

    try {
      await apiFetch("/api/me/password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: {
          currentPassword,
          newPassword,
        },
      });

      alert("Passwort wurde geändert.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      console.error("Fehler beim Ändern des Passworts", e);
      alert("Passwort konnte nicht geändert werden. Prüfe dein aktuelles Passwort.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Header mit Kundennummer */}
      <MDBox mt={1} mb={3}>
        <MDTypography variant="h4" fontWeight="medium" mb={0.5}>
          Profil &amp; Abrechnung
        </MDTypography>
        <MDBox display="flex" alignItems="center" gap={1.5} my={1}>
          <Avatar
            src={resolveAssetUrl(profile.avatarUrl)}
            alt={profile.contactName || profile.companyName || "Profil"}
            sx={{ width: 64, height: 64 }}
          />
          <Button variant="outlined" component="label" disabled={uploadingAvatar}>
            {uploadingAvatar ? "Lädt..." : "Profilbild hochladen"}
            <input hidden accept="image/*" type="file" onChange={handleAvatarUpload} />
          </Button>
        </MDBox>
        {isAdmin ? (
          <MDTypography variant="button" color="text">
            Admin-Account: {userEmail || "–"}
          </MDTypography>
        ) : (
          <MDTypography variant="button" color="text">
            Kundennummer: {profile.customerNumber || "–"}
          </MDTypography>
        )}
        {savedAt && (
          <MDTypography variant="caption" color="text">
            Zuletzt gespeichert: {savedAt.toLocaleString()}
          </MDTypography>
        )}
      </MDBox>

      {/* Zugangsdaten: Kundennummer + Passwort ändern */}
      <MDBox mb={3} p={3} borderRadius="lg" bgColor="white" shadow="sm">
        <MDTypography variant="h6" fontWeight="medium" mb={2}>
          Zugangsdaten
        </MDTypography>

       

        <MDBox component="form" onSubmit={handleChangePassword}>
          <MDTypography variant="button" color="text" mb={1}>
            Passwort ändern
          </MDTypography>

          <MDBox
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
            mb={2}
          >
            <MDInput
              type="password"
              label="Aktuelles Passwort"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
            />
            <MDInput
              type="password"
              label="Neues Passwort"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
            />
            <MDInput
              type="password"
              label="Neues Passwort bestätigen"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
            />
          </MDBox>

          <MDButton variant="outlined" color="info" type="submit">
            Passwort ändern
          </MDButton>
        </MDBox>
      </MDBox>

      {!isAdmin && (
        <>
          {/* Rechnungsadresse */}
          <MDBox mb={3} p={3} borderRadius="lg" bgColor="white" shadow="sm">
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Rechnungsadresse
            </MDTypography>

            <MDBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
              gap={2}
            >
              <MDInput
                label="Firmenname"
                value={profile.companyName}
                onChange={handleChange("companyName")}
                fullWidth
              />
              <MDInput
                label="Ansprechperson"
                value={profile.contactName}
                onChange={handleChange("contactName")}
                fullWidth
              />
              <MDInput
                label="Straße & Hausnummer"
                value={profile.street}
                onChange={handleChange("street")}
                fullWidth
              />
              <MDInput
                label="PLZ"
                value={profile.zip}
                onChange={handleChange("zip")}
                fullWidth
              />
              <MDInput
                label="Ort"
                value={profile.city}
                onChange={handleChange("city")}
                fullWidth
              />
              <MDInput
                label="Land"
                value={profile.country}
                onChange={handleChange("country")}
                fullWidth
              />
              <MDInput
                label="USt-IdNr. (optional)"
                value={profile.vatId}
                onChange={handleChange("vatId")}
                fullWidth
              />
            </MDBox>
          </MDBox>

          {/* Zahlungsinformationen */}
          <MDBox mb={3} p={3} borderRadius="lg" bgColor="white" shadow="sm">
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Zahlungsinformationen
            </MDTypography>

            <MDBox mb={2}>
              <MDTypography variant="button" color="text">
                Zahlungsart: Lastschrift
              </MDTypography>
            </MDBox>

            <MDBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
              gap={2}
            >
              <MDInput
                label="Kontoinhaber"
                value={profile.accountHolder}
                onChange={handleChange("accountHolder")}
                fullWidth
              />
              <MDInput
                label="IBAN"
                value={profile.iban}
                onChange={handleChange("iban")}
                fullWidth
              />
              <MDInput
                label="BIC"
                value={profile.bic}
                onChange={handleChange("bic")}
                fullWidth
              />
            </MDBox>
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end" mb={6}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSaveProfile}
              disabled={saving}
            >
              {saving ? "Speichern..." : "Änderungen speichern"}
            </MDButton>
          </MDBox>
        </>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default Overview;
